import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Grid,
  Button,
  Typography,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  IconButton,
  TextField,
  MenuItem,
  Stack
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DriveFileMoveIcon from "@mui/icons-material/DriveFileMove";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import EditIcon from "@mui/icons-material/Edit";
import { DndContext, DragOverlay } from "@dnd-kit/core";
import FieldPalette from "./components/FieldPalette";
import ReportCanvas from "./components/ReportCanvas";
import PropertiesPanel from "./components/PropertiesPanel";
import ReportPreview from "./components/ReportPreview";
import { createElementFromDrag } from "./reportDesignerUtils";
import { collectAvailableFieldPaths } from "./reportFieldAvailability";
import { getReportDesignerActions } from "./reportDesignerPersistence";

import axios from "axios";
import { baseURL } from "../../api";
import { Global_Data } from "../../globalData/GlobalData";
import NewReportDialog from "./components/NewReportDialog";
import ReportFieldConfigurationDialog from "./components/ReportFieldConfigurationDialog";
import { toast } from "react-toastify";
import CustomAlert from "../../component/AlertComponent/Alert";

const PAGE_SIZES = {
  A3: { width: 842, height: 1191, label: "A3" },
  A4: { width: 595, height: 842, label: "A4" },
  A5: { width: 420, height: 595, label: "A5" },
  LETTER: { width: 612, height: 792, label: "Letter" },
  LEGAL: { width: 612, height: 1008, label: "Legal" }
};

const initialTemplate = {
  schemaVersion: 2,
  reportKind: "FORM",
  reportCode: "",
  reportName: "New Report",
  description: "",
  dataSource: {
    type: "FORM",
    formType: "",
    transactionType: "",
    transactionName: "",
    companyId: "",
    companyName: ""
  },
  filters: [],
  page: {
    size: "A4",
    orientation: "PORTRAIT",
    width: 595,
    height: 842,
    margin: 20
  },
  bands: {
    summary: {
      y: 430,
      height: 120
    }
  },
  elements: []
};

export default function ReportDesigner() {
  const { token } = Global_Data?.() || {};
  const authHeaders = useMemo(() => {
    return token
      ? { Authorization: `Bearer ${token}` }
      : {};
  }, [token]);

  const [template, setTemplate] = useState(initialTemplate);
  const [selectedIds, setSelectedIds] = useState([]);
  const [previewOpen, setPreviewOpen] = useState(false);

  const [designs, setDesigns] = useState([]);
  const [systemTemplates, setSystemTemplates] = useState([]);
  const [sourceAuthoringEnabled, setSourceAuthoringEnabled] = useState(false);
  const [queryAuthoringEnabled, setQueryAuthoringEnabled] = useState(false);
  const [selectedDesignId, setSelectedDesignId] = useState("");
  const [selectedOwnership, setSelectedOwnership] = useState("");
  const [selectedTemplateCode, setSelectedTemplateCode] = useState("");
  const [reportId, setReportId] = useState("");
  const [fieldGroups, setFieldGroups] = useState({});
  const [newDialogOpen, setNewDialogOpen] = useState(false);
  const [previewData, setPreviewData] = useState({});
  const [activeDragData, setActiveDragData] = useState(null);
  const [cloneDialogOpen, setCloneDialogOpen] = useState(false);
  const [moveConfirmationOpen, setMoveConfirmationOpen] = useState(false);
  const [moveInProgress, setMoveInProgress] = useState(false);
  const [fieldConfigurationOpen, setFieldConfigurationOpen] = useState(false);
  const [pagePropertiesOpen, setPagePropertiesOpen] = useState(false);
  const [renameDialogOpen, setRenameDialogOpen] = useState(false);
  const [renameValue, setRenameValue] = useState("");
  const [renameInProgress, setRenameInProgress] = useState(false);
  const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);
  const [deleteInProgress, setDeleteInProgress] = useState(false);

  const selectedId = selectedIds[selectedIds.length - 1] || null;
  const selectedElement = template.elements?.find(e => e.id === selectedId);
  const persistenceActions = getReportDesignerActions({
    reportId: selectedDesignId,
    ownership: selectedOwnership,
    sourceAuthoringEnabled
  });
  const sourceReadOnly = persistenceActions.readOnly;
  const availableFieldPaths = useMemo(
    () => collectAvailableFieldPaths(fieldGroups),
    [fieldGroups]
  );
  const fieldValidationEnabled = Object.keys(fieldGroups || {}).length > 0;

  const loadDesignerFields = async (transactionType, companyId, reportType = "FORM") => {
    if (!transactionType || (reportType === "FORM" && !companyId)) return;

    try {
      const fieldUrl = reportType === "COLUMNAR"
        ? `${baseURL}/report/designs/columnar/fields/${transactionType}`
        : `${baseURL}/report/designs/fields/${transactionType}/${companyId}`;
      const res = await axios.get(
        fieldUrl,
        { headers: authHeaders }
      );

      setFieldGroups(res?.data?.data?.fieldGroups || {});
      setPreviewData(res?.data?.data?.previewData || {});
    } catch (error) {
      console.error("Failed to load designer fields", error);
      CustomAlert("error",
        error?.response?.data?.metadata?.msg || "Failed to load report fields."
      );
    }
  };

  const loadDesigns = async () => {
    try {
      const res = await axios.get(`${baseURL}/report/designs`, {
        headers: authHeaders
      });

      setDesigns(res?.data?.data?.records || []);
    } catch (error) {
      console.error("Failed to load report designs", error);
      CustomAlert("error",
        error?.response?.data?.metadata?.msg || "Failed to save report design."
      );
    }
  };

  const loadSystemTemplates = async () => {
    try {
      const res = await axios.get(`${baseURL}/report/templates`, {
        headers: authHeaders
      });
      setSystemTemplates(res?.data?.data?.records || []);
    } catch (error) {
      console.error("Failed to load system templates", error);
      CustomAlert("error",
        error?.response?.data?.metadata?.msg || "Failed to load system templates."
      );
    }
  };

  const loadConfiguration = async () => {
    try {
      const res = await axios.get(`${baseURL}/report/designer/config`, {
        headers: authHeaders
      });
      setSourceAuthoringEnabled(Boolean(res?.data?.data?.sourceAuthoringEnabled));
      setQueryAuthoringEnabled(Boolean(res?.data?.data?.queryAuthoringEnabled));
    } catch (error) {
      console.error("Failed to load Report Designer configuration", error);
      setSourceAuthoringEnabled(false);
      setQueryAuthoringEnabled(false);
    }
  };

  const loadDesign = async (id, ownership = "USER") => {
    if (!id) return;

    try {
      const collection = ownership === "SYSTEM" ? "templates" : "designs";
      const res = await axios.get(`${baseURL}/report/${collection}/${id}`, {
        headers: authHeaders
      });

      const record = res?.data?.data;

      const parsedTemplate =
        typeof record.sTemplateJSON === "string"
          ? JSON.parse(record.sTemplateJSON)
          : record.sTemplateJSON;

      setSelectedDesignId(record.sReportID);
      setSelectedOwnership(record.sOwnership || ownership);
      setSelectedTemplateCode(record.sTemplateCode || record.sSourceTemplateCode || "");
      setReportId(record.sReportID || "");
      const reportType = String(
        record.sReportType || parsedTemplate?.reportKind || parsedTemplate?.dataSource?.type || "FORM"
      ).toUpperCase();
      const savedPageSize = parsedTemplate?.page?.size || record.sPaperSize || "A4";
      const savedOrientation =
        parsedTemplate?.page?.orientation || record.sOrientation || "PORTRAIT";
      const savedPageDefinition = PAGE_SIZES[savedPageSize] || PAGE_SIZES.A4;
      setTemplate({
        ...initialTemplate,
        ...parsedTemplate,
        reportName: record.sReportName || parsedTemplate?.reportName || "Untitled Report",
        reportKind: reportType,
        dataSource: {
          ...initialTemplate.dataSource,
          ...parsedTemplate?.dataSource,
          type: reportType,
          formType: record.sFormType || parsedTemplate?.dataSource?.formType || "",
          transactionType: record.sFormType || parsedTemplate?.dataSource?.transactionType || "",
          companyId: record.sEntity || parsedTemplate?.dataSource?.companyId || ""
        },
        page: {
          ...initialTemplate.page,
          ...(parsedTemplate?.page || {}),
          size: savedPageSize,
          orientation: savedOrientation,
          width: parsedTemplate?.page?.width || (
            savedOrientation === "LANDSCAPE"
              ? savedPageDefinition.height
              : savedPageDefinition.width
          ),
          height: parsedTemplate?.page?.height || (
            savedOrientation === "LANDSCAPE"
              ? savedPageDefinition.width
              : savedPageDefinition.height
          )
        },
        filters: parsedTemplate?.filters || [],
        elements: parsedTemplate?.elements || []
      });
      setSelectedIds([]);
      await loadDesignerFields(
        record.sFormType || parsedTemplate?.dataSource?.transactionType,
        record.sEntity || parsedTemplate?.dataSource?.companyId,
        reportType
      );
    } catch (error) {
      console.error("Failed to open report design", error);
      CustomAlert("error",
        error?.response?.data?.metadata?.msg || "Failed to save report design."
      );
    }
  };

  const handleDragStart = event => {
    setActiveDragData(event.active?.data?.current || null);
  };

  const buildSavePayload = () => {
    const templateToSave = {
      ...template,
      dataSource: { ...template.dataSource }
    };
    return {
      templateToSave,
      payload: {
        sTemplateCode:
          selectedTemplateCode || makeReportCode(templateToSave.reportName),
        sReportName: templateToSave.reportName || "New Report",
        sReportType: templateToSave.reportKind || templateToSave.dataSource?.type || "FORM",
        sDescription: templateToSave.description || "",
        sFormType:
          templateToSave.dataSource?.transactionType ||
          templateToSave.dataSource?.formType ||
          "",
        sCompanyID: templateToSave.dataSource?.companyId || "",
        sOrientation: templateToSave.page?.orientation || "PORTRAIT",
        sPaperSize: templateToSave.page?.size || "A4",
        sTemplateJSON: JSON.stringify(templateToSave)
      }
    };
  };

  const saveTemplate = async targetOwnership => {
  try {
    const { templateToSave, payload } = buildSavePayload();

    let res;

    if (selectedDesignId) {
      if (sourceReadOnly) {
        CustomAlert("warning", "Source authoring is disabled. Clone this template to customize it.");
        return;
      }
      const collection = selectedOwnership === "SYSTEM" ? "templates" : "designs";
      res = await axios.put(
        `${baseURL}/report/${collection}/${selectedDesignId}`,
        payload,
        { headers: authHeaders }
      );
    } else {
      const destination = targetOwnership === "SYSTEM" ? "templates" : "designs";
      res = await axios.post(
        `${baseURL}/report/${destination}`,
        payload,
        { headers: authHeaders }
      );
    }

    const savedRecord = res?.data?.data || {};
    const savedReportId = savedRecord.sReportID || selectedDesignId;

    setSelectedDesignId(savedReportId);
    setSelectedOwnership(savedRecord.sOwnership || targetOwnership || "USER");
    setSelectedTemplateCode(
      savedRecord.sTemplateCode ||
      savedRecord.sSourceTemplateCode ||
      payload.sTemplateCode
    );
    setReportId(savedReportId);

    setTemplate({
      ...templateToSave,
      reportCode: savedReportId
    });

    await Promise.all([loadDesigns(), loadSystemTemplates()]);

    toast.success(
      res?.data?.metadata?.msg || "Report design saved."
    );
  } catch (error) {
    console.error("Failed to save report design", error);
    CustomAlert("error",
      error?.response?.data?.metadata?.msg ||
      "Failed to save report design."
    );
  }
};

  const loadMetadata = async () => {
    const transactionType = template.dataSource?.transactionType;
    const companyId = template.dataSource?.companyId;

    const reportType = template.reportKind || template.dataSource?.type || "FORM";
    if (!transactionType || (reportType === "FORM" && !companyId)) {
      CustomAlert("warning", "Create or open a report with the required data source first.");
      return;
    }

    try {
      const fieldUrl = reportType === "COLUMNAR"
        ? `${baseURL}/report/designs/columnar/fields/${transactionType}`
        : `${baseURL}/report/designs/fields/${transactionType}/${companyId}`;
      const res = await axios.get(
        fieldUrl,
        { headers: authHeaders }
      );

      setFieldGroups(res?.data?.data?.fieldGroups || {});
      toast.success("Fields loaded.");
    } catch (error) {
      console.error("Failed to load fields", error);
      CustomAlert("error",
        error?.response?.data?.metadata?.msg || "Failed to load fields."
      );
    }
  };

  useEffect(() => {
  if (token) {
    Promise.all([loadConfiguration(), loadDesigns(), loadSystemTemplates()]);
  }
}, [token]);

  const handleDragEnd = event => {
    if (sourceReadOnly) {
      setActiveDragData(null);
      return;
    }
    const { active, over } = event;

    setActiveDragData(null);

    if (!over || over.id !== "report-canvas") return;

    const canvasElement = document.querySelector("[data-report-canvas='true']");
    if (!canvasElement) return;

    const canvasRect = canvasElement.getBoundingClientRect();
    const zoom = 1.6;

    const translatedRect = active?.rect?.current?.translated;

    if (!translatedRect) {
      return;
    }

    const newElementBase = createElementFromDrag(active.data.current);

    const dropX = Math.round(
      (translatedRect.left - canvasRect.left) / zoom
    );

    const dropY = Math.round(
      (translatedRect.top - canvasRect.top) / zoom
    );

    /*
    * Only one Summary Band is allowed.
    */
    if (
      newElementBase.type === "summaryBand" &&
      (template.elements || []).some(
        element => element.type === "summaryBand"
      )
    ) {
      CustomAlert("warning", "Only one Summary Band is allowed.");
      return;
    }

    const summaryBand = (template.elements || []).find(
      element => element.type === "summaryBand"
    );

    const insideSummaryBand =
      Boolean(summaryBand) &&
      newElementBase.type !== "summaryBand" &&
      dropX >= summaryBand.x &&
      dropX <= summaryBand.x + summaryBand.width &&
      dropY >= summaryBand.y &&
      dropY <= summaryBand.y + summaryBand.height;

    const newElement = {
      ...newElementBase,

      /*
      * Summary children store coordinates relative to the band.
      * Normal elements store coordinates relative to the page.
      */
      band: insideSummaryBand ? "summary" : "page",

      x: insideSummaryBand
        ? Math.max(0, dropX - summaryBand.x)
        : Math.max(0, dropX),

      y: insideSummaryBand
        ? Math.max(0, dropY - summaryBand.y)
        : Math.max(0, dropY)
    };

    setTemplate(prev => ({
      ...prev,
      elements: [...(prev.elements || []), newElement]
    }));

    setSelectedIds([newElement.id]);
  };

  const handleDragCancel = () => {
    setActiveDragData(null);
  };

  const updateElement = updated => {
    setTemplate(prev => {
      const elements = prev.elements || [];

      if ((prev.reportKind || prev.dataSource?.type) === "COLUMNAR" && updated.type === "field") {
        const path = updated.fieldPath;
        let nextElements = elements.map(element => element.id === updated.id ? updated : element);
        let table = nextElements.find(element => element.type === "columnarTable");
        if (updated.useAsColumn) {
          if (!table) {
            table = createElementFromDrag({ type: "columnarTable" });
            table.x = 30;
            table.y = 160;
            nextElements = [...nextElements, table];
          }
          if (!(table.columns || []).some(column => column.fieldPath === path)) {
            const columns = [...(table.columns || []), {
              label: updated.label,
              fieldPath: path,
              fieldType: updated.fieldType,
              dataType: updated.dataType,
              width: 120,
              visible: true,
              headerAlign: "center",
              detailAlign: "left",
              showHeaderCellLines: true,
              showDetailCellLines: true
            }];
            nextElements = nextElements.map(element =>
              element.id === table.id ? { ...table, columns } : element
            );
          }
        } else if (table) {
          nextElements = nextElements.map(element =>
            element.id === table.id
              ? { ...element, columns: (element.columns || []).filter(column => column.fieldPath !== path) }
              : element
          );
        }

        const otherFilters = (prev.filters || []).filter(filter => filter.fieldPath !== path);
        const filters = updated.useAsFilter
          ? [...otherFilters, {
              fieldPath: path,
              field: path,
              label: updated.label,
              dataType: updated.dataType || updated.fieldType || "VARCHAR",
              required: Boolean(updated.filterRequired),
              defaultOperator: updated.filterOperator ||
                (["DATE", "DATETIME", "CREATEDATETIME", "UPDATEDATETIME"].includes(
                  String(updated.dataType || updated.fieldType || "").toUpperCase()
                ) ? "BETWEEN" : "EQUALS"),
              displayOrder: otherFilters.length + 1
            }]
          : otherFilters;
        return { ...prev, elements: nextElements, filters };
      }

      const summaryBand = elements.find(
        element => element.type === "summaryBand"
      );

      /*
      * The Summary Band itself remains a page-level element.
      */
      if (updated.type === "summaryBand") {
        return {
          ...prev,
          elements: elements.map(element =>
            element.id === updated.id
              ? {
                  ...updated,
                  band: "page"
                }
              : element
          )
        };
      }

      /*
      * ReportCanvas passes logical coordinates:
      * - relative coordinates for summary children
      * - page coordinates for normal elements
      *
      * Therefore, ordinary property changes can be saved directly.
      */
      return {
        ...prev,
        elements: elements.map(element =>
          element.id === updated.id ? updated : element
        )
      };
    });
  };

  const updateElements = updatedElements => {
    if (!Array.isArray(updatedElements) || updatedElements.length === 0) return;

    const updatesById = new Map(
      updatedElements.map(element => [element.id, element])
    );

    setTemplate(prev => ({
      ...prev,
      elements: (prev.elements || []).map(element =>
        updatesById.get(element.id) || element
      )
    }));
  };

  const deleteElements = ids => {
    const idsToDelete = new Set(Array.isArray(ids) ? ids : [ids]);

    setTemplate(prev => ({
      ...prev,
      elements: (prev.elements || []).filter(e => !idsToDelete.has(e.id))
    }));
    setSelectedIds([]);
  };

  const deleteElement = id => {
    deleteElements(selectedIds.includes(id) ? selectedIds : [id]);
  };

  const handleNew = () => {
    setNewDialogOpen(true);
  };

  const makeReportCode = name => {
    const base = (name || "REPORT")
      .toUpperCase()
      .replace(/[^A-Z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "");

    return base || "REPORT";
  };

  const handleCreateNewReport = async reportInfo => {
  const newTemplate = {
      ...initialTemplate,
      reportKind: reportInfo.formType,
      reportCode: makeReportCode(reportInfo.reportName),
      reportName: reportInfo.reportName,
      description: reportInfo.description,
      dataSource: {
        ...initialTemplate.dataSource,
        type: reportInfo.formType,
        formType: reportInfo.transactionType,
        transactionType: reportInfo.transactionType,
        transactionName: reportInfo.transactionName,
        companyId: reportInfo.companyId,
        companyName: reportInfo.companyName
      },
      filters: [],
      elements: []
    };

    setTemplate(newTemplate);
    setSelectedDesignId("");
    setSelectedOwnership("");
    setSelectedTemplateCode(newTemplate.reportCode);
    setReportId(newTemplate.reportCode);
    setFieldGroups({});
    setPreviewData({});
    setSelectedIds([]);
    setNewDialogOpen(false);

    await loadDesignerFields(reportInfo.transactionType, reportInfo.companyId, reportInfo.formType);
  };

  const handleCloneReport = async reportInfo => {
    if (!selectedDesignId) {
      CustomAlert("warning", "Open a report first before cloning.");
      return;
    }

    try {
      const payload = {
        sReportName: reportInfo.reportName,
        sDescription: reportInfo.description,
        sReportType: template.reportKind || template.dataSource?.type || "FORM",
        sFormType: reportInfo.transactionType,
        sCompanyID: reportInfo.companyId
      };

      const collection = selectedOwnership === "SYSTEM" ? "templates" : "designs";
      const res = await axios.post(
        `${baseURL}/report/${collection}/${selectedDesignId}/clone`,
        payload,
        { headers: authHeaders }
      );

      const clonedRecord = res?.data?.data;

      toast.success(res?.data?.metadata?.msg || "Report cloned.");

      setCloneDialogOpen(false);

      await Promise.all([loadDesigns(), loadSystemTemplates()]);

      if (clonedRecord?.sReportID) {
        await loadDesign(clonedRecord.sReportID, "USER");
      }
    } catch (error) {
      console.error("Failed to clone report design", error);
      CustomAlert("error",
        error?.response?.data?.metadata?.msg ||
        "Failed to clone report design."
      );
    }
  };

  const moveToTemplate = async () => {
    if (!selectedDesignId || selectedOwnership !== "USER" || moveInProgress) return;

    setMoveInProgress(true);
    try {
      const { payload } = buildSavePayload();
      const res = await axios.post(
        `${baseURL}/report/designs/${selectedDesignId}/move-to-template`,
        {
          sTemplateCode: `${makeReportCode(payload.sReportName)}_${selectedDesignId}`
        },
        { headers: authHeaders }
      );
      const moved = res?.data?.data || {};
      await Promise.all([loadDesigns(), loadSystemTemplates()]);
      if (moved.sReportID) {
        await loadDesign(moved.sReportID, "SYSTEM");
      }
      toast.success(res?.data?.metadata?.msg || "Report moved to System Templates.");
    } catch (error) {
      console.error("Failed to move report to template", error);
      CustomAlert("error",
        error?.response?.data?.metadata?.msg || "Failed to move report to System Templates."
      );
    } finally {
      setMoveInProgress(false);
      setMoveConfirmationOpen(false);
    }
  };

  const closeMoveConfirmation = () => {
    if (!moveInProgress) {
      setMoveConfirmationOpen(false);
    }
  };

  const openRenameDialog = () => {
    setRenameValue(template.reportName || "");
    setRenameDialogOpen(true);
  };

  const closeRenameDialog = () => {
    if (!renameInProgress) {
      setRenameDialogOpen(false);
    }
  };

  const renameReport = async () => {
    const reportName = renameValue.trim();
    if (!selectedDesignId || !persistenceActions.rename || !reportName || renameInProgress) {
      return;
    }

    setRenameInProgress(true);
    try {
      const collection = selectedOwnership === "SYSTEM" ? "templates" : "designs";
      const response = await axios.patch(
        `${baseURL}/report/${collection}/${selectedDesignId}/name`,
        { sReportName: reportName },
        { headers: authHeaders }
      );

      setTemplate(previous => ({ ...previous, reportName }));
      await Promise.all([loadDesigns(), loadSystemTemplates()]);
      toast.success(response?.data?.metadata?.msg || "Report renamed.");
      setRenameDialogOpen(false);
    } catch (error) {
      console.error("Failed to rename report", error);
      CustomAlert(
        "error",
        error?.response?.data?.metadata?.msg || "Failed to rename report."
      );
    } finally {
      setRenameInProgress(false);
    }
  };

  const closeDeleteConfirmation = () => {
    if (!deleteInProgress) {
      setDeleteConfirmationOpen(false);
    }
  };

  const deleteReport = async () => {
    if (!selectedDesignId || !persistenceActions.delete || deleteInProgress) return;

    setDeleteInProgress(true);
    try {
      const collection = selectedOwnership === "SYSTEM" ? "templates" : "designs";
      const response = await axios.delete(
        `${baseURL}/report/${collection}/${selectedDesignId}`,
        { headers: authHeaders }
      );

      setTemplate(initialTemplate);
      setSelectedDesignId("");
      setSelectedOwnership("");
      setSelectedTemplateCode("");
      setReportId("");
      setFieldGroups({});
      setPreviewData({});
      setSelectedIds([]);
      await Promise.all([loadDesigns(), loadSystemTemplates()]);
      toast.success(response?.data?.metadata?.msg || "Report deleted.");
      setDeleteConfirmationOpen(false);
    } catch (error) {
      console.error("Failed to delete report", error);
      CustomAlert(
        "error",
        error?.response?.data?.metadata?.msg || "Failed to delete report."
      );
    } finally {
      setDeleteInProgress(false);
    }
  };

  const updatePageProperties = (size, orientation) => {
    const definition = PAGE_SIZES[size] || PAGE_SIZES.A4;
    const landscape = orientation === "LANDSCAPE";

    setTemplate(previous => ({
      ...previous,
      page: {
        ...(previous.page || {}),
        size,
        orientation,
        width: landscape ? definition.height : definition.width,
        height: landscape ? definition.width : definition.height
      }
    }));
  };

  return (
    <Box sx={{
      p: 3,
      width: "100%",
      height: "calc(100vh - 72px)",
      boxSizing: "border-box",
      display: "flex",
      flexDirection: "column",
      overflow: "hidden"
    }}>
      <Box sx={{ mb: 2, flexShrink: 0 }}>
        <Typography variant="h4">
          {template.reportName || "Report Designer"}
        </Typography>

        {template.description && (
          <Typography variant="body2">
            {template.description}
          </Typography>
        )}

        <Typography variant="caption" color="text.secondary">
          Type: {template.dataSource?.type || "-"}
          {" | "}
          Transaction: {template.dataSource?.transactionName || template.dataSource?.transactionType || "-"}
          {" | "}
          Company: {template.dataSource?.companyName || template.dataSource?.companyId || "-"}
        </Typography>
      </Box>

      <DndContext
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          onDragCancel={handleDragCancel}
        >
        <Grid container spacing={2} sx={{
          width: "100%",
          flex: 1,
          minHeight: 0,
          overflow: "hidden"
        }}>
          <Grid item xs={2} sx={{ height: "100%", minHeight: 0, overflow: "hidden" }}>
            <Box sx={{ height: "100%", overflowY: "auto", overflowX: "hidden", pr: 0.5 }}>
              <FieldPalette
              fieldGroups={fieldGroups}
              designs={designs}
              templates={systemTemplates}
              selectedDesignId={selectedDesignId}
              selectedOwnership={selectedOwnership}
              onNew={handleNew}
              onOpenDesign={id => loadDesign(id, "USER")}
              onOpenTemplate={id => loadDesign(id, "SYSTEM")}
              readOnly={sourceReadOnly}
              reportType={template.reportKind || template.dataSource?.type}
              />
            </Box>
          </Grid>

          <Grid item xs={8} sx={{ height: "100%", minHeight: 0, overflow: "hidden" }}>
            <Box sx={{ height: "100%", overflow: "hidden" }}>
              <ReportCanvas
              template={template}
              previewData={previewData}
              availableFieldPaths={availableFieldPaths}
              fieldValidationEnabled={fieldValidationEnabled}
              selectedIds={selectedIds}
              onSelectionChange={setSelectedIds}
              onUpdateElement={updateElement}
              onUpdateElements={updateElements}
              onDeleteElements={deleteElements}
              readOnly={sourceReadOnly}
              />
            </Box>
          </Grid>

          <Grid item xs={2} sx={{ height: "100%", minHeight: 0, overflow: "hidden" }}>
            <Box sx={{ height: "100%", overflowY: "auto", overflowX: "hidden", pl: 0.5 }}>
              {sourceReadOnly ? (
              <Paper sx={{ p: 2, boxSizing: "border-box" }}>
                <Typography variant="subtitle2">Read-only System Template</Typography>
                <Typography variant="body2" color="text.secondary">
                  Clone & Customize to create an editable report.
                </Typography>
              </Paper>
              ) : (
              <PropertiesPanel
                key={selectedElement?.id || "no-selection"}
                element={selectedElement}
                fieldGroups={fieldGroups}
                reportType={template.reportKind || template.dataSource?.type}
                queryAuthoringEnabled={queryAuthoringEnabled}
                onChange={updateElement}
                onDelete={deleteElement}
              />
              )}
            </Box>
          </Grid>
        </Grid>
        <DragOverlay dropAnimation={null}>
          {activeDragData ? (
            <Box
              sx={{
                px: 1.5,
                py: 0.75,
                border: "1px solid",
                borderColor: "primary.main",
                bgcolor: "background.paper",
                boxShadow: 3,
                borderRadius: 1,
                fontSize: 12,
                pointerEvents: "none"
              }}
            >
              {activeDragData.label || activeDragData.text || activeDragData.type}
            </Box>
          ) : null}
        </DragOverlay>
      </DndContext>

      <Paper sx={{
        mt: 2,
        p: 2,
        display: "flex",
        gap: 1,
        flexWrap: "wrap",
        flexShrink: 0,
        position: "sticky",
        bottom: 0,
        zIndex: 20,
        bgcolor: "background.paper",
        boxShadow: 4
      }}>
        {persistenceActions.saveAsUserReport && (
          <Button variant="contained" onClick={() => saveTemplate("USER")}>
            Save as User Report
          </Button>
        )}

        {persistenceActions.saveAsTemplate && (
          <Button variant="outlined" onClick={() => saveTemplate("SYSTEM")}>
            Save as Template
          </Button>
        )}

        {persistenceActions.save && (
          <Button variant="contained" onClick={() => saveTemplate(selectedOwnership)}>
            Save
          </Button>
        )}

        {persistenceActions.moveToTemplate && (
          <Button
            color="warning"
            variant="outlined"
            onClick={() => setMoveConfirmationOpen(true)}
          >
            Move to Template
          </Button>
        )}

        {persistenceActions.rename && (
          <Button
            variant="outlined"
            startIcon={<EditIcon />}
            onClick={openRenameDialog}
          >
            Rename Report
          </Button>
        )}

        {persistenceActions.delete && (
          <Button
            color="error"
            variant="outlined"
            startIcon={<DeleteForeverIcon />}
            onClick={() => setDeleteConfirmationOpen(true)}
          >
            Delete Report
          </Button>
        )}

        {sourceAuthoringEnabled && template.dataSource?.transactionType && template.reportKind !== "COLUMNAR" && (
          <Button
            variant="outlined"
            onClick={() => setFieldConfigurationOpen(true)}
          >
            Configure Fields
          </Button>
        )}

        <Button
          variant="outlined"
          disabled={!persistenceActions.clone}
          onClick={() => setCloneDialogOpen(true)}
        >
          {selectedOwnership === "SYSTEM" ? "Clone & Customize" : "Clone Report"}
        </Button>

        <Button variant="outlined" onClick={() => setPreviewOpen(true)}>
          Preview HTML
        </Button>

        <Button variant="outlined" onClick={() => setPagePropertiesOpen(true)}>
          Page Properties
        </Button>

        <Button variant="outlined" disabled>
          Export PDF
        </Button>

        <Button variant="outlined" disabled>
          Export CSV
        </Button>
      </Paper>

      <Dialog
        open={pagePropertiesOpen}
        onClose={() => setPagePropertiesOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Page Properties</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              select
              label="Page Size"
              value={template.page?.size || "A4"}
              onChange={event => updatePageProperties(
                event.target.value,
                template.page?.orientation || "PORTRAIT"
              )}
              fullWidth
            >
              {Object.entries(PAGE_SIZES).map(([value, definition]) => (
                <MenuItem key={value} value={value}>
                  {definition.label}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              select
              label="Orientation"
              value={template.page?.orientation || "PORTRAIT"}
              onChange={event => updatePageProperties(
                template.page?.size || "A4",
                event.target.value
              )}
              fullWidth
            >
              <MenuItem value="PORTRAIT">Portrait</MenuItem>
              <MenuItem value="LANDSCAPE">Landscape</MenuItem>
            </TextField>

            <Typography variant="body2" color="text.secondary">
              Canvas: {template.page?.width || 595} × {template.page?.height || 842} points
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={() => setPagePropertiesOpen(false)}>
            Done
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={renameDialogOpen}
        onClose={closeRenameDialog}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Rename Report</DialogTitle>
        <DialogContent dividers>
          <TextField
            autoFocus
            fullWidth
            label="Report Name"
            value={renameValue}
            onChange={event => setRenameValue(event.target.value)}
            onKeyDown={event => {
              if (event.key === "Enter" && renameValue.trim()) {
                event.preventDefault();
                renameReport();
              }
            }}
            inputProps={{ maxLength: 255 }}
            disabled={renameInProgress}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={closeRenameDialog} disabled={renameInProgress}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={renameReport}
            disabled={!renameValue.trim() || renameInProgress}
          >
            {renameInProgress ? "Renaming..." : "Rename"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteConfirmationOpen}
        onClose={closeDeleteConfirmation}
        aria-labelledby="delete-report-confirmation-title"
        aria-describedby="delete-report-confirmation-description"
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle
          id="delete-report-confirmation-title"
          sx={{ m: 0, p: 2, pr: 6, fontSize: 16, fontWeight: 600 }}
        >
          Confirmation
        </DialogTitle>
        <IconButton
          aria-label="close"
          onClick={closeDeleteConfirmation}
          disabled={deleteInProgress}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: theme => theme.palette.grey[500]
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
        <DialogContent dividers>
          <DialogContentText id="delete-report-confirmation-description">
            Delete “{template.reportName || "this report"}”? It will be archived and
            removed from {selectedOwnership === "SYSTEM" ? "System Templates" : "My Reports"}.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "flex-start", p: 2 }}>
          <Button
            variant="contained"
            color="error"
            onClick={deleteReport}
            disabled={deleteInProgress}
            endIcon={<DeleteForeverIcon />}
          >
            {deleteInProgress ? "DELETING..." : "DELETE REPORT"}
          </Button>
          <Button
            variant="outlined"
            onClick={closeDeleteConfirmation}
            disabled={deleteInProgress}
          >
            CANCEL
          </Button>
        </DialogActions>
      </Dialog>

      <ReportPreview
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        template={template}
        reportId={selectedDesignId}
        authHeaders={authHeaders}
      />

      <NewReportDialog
        open={newDialogOpen}
        onClose={() => setNewDialogOpen(false)}
        onCreate={handleCreateNewReport}
      />

      <NewReportDialog
        open={cloneDialogOpen}
        onClose={() => setCloneDialogOpen(false)}
        onCreate={handleCloneReport}
        defaultReportType={template.reportKind || template.dataSource?.type || "FORM"}
        lockReportType
        defaultTransactionType={template.dataSource?.transactionType || ""}
        defaultCompanyId={template.dataSource?.companyId || ""}
      />

      <Dialog
        open={moveConfirmationOpen}
        onClose={closeMoveConfirmation}
        aria-labelledby="move-to-template-confirmation-title"
        aria-describedby="move-to-template-confirmation-description"
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle
          id="move-to-template-confirmation-title"
          sx={{ m: 0, p: 2, pr: 6, fontSize: 16, fontWeight: 600 }}
        >
          Confirmation
        </DialogTitle>
        <IconButton
          aria-label="close"
          onClick={closeMoveConfirmation}
          disabled={moveInProgress}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: theme => theme.palette.grey[500]
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
        <DialogContent dividers>
          <DialogContentText id="move-to-template-confirmation-description">
            Move this report to System Templates? It will be removed from My Reports
            and may become read-only when source authoring is disabled.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "flex-start", p: 2 }}>
          <Button
            variant="contained"
            color="error"
            onClick={moveToTemplate}
            disabled={moveInProgress}
            endIcon={<DriveFileMoveIcon />}
          >
            {moveInProgress ? "MOVING..." : "MOVE TO TEMPLATE"}
          </Button>
          <Button
            variant="outlined"
            onClick={closeMoveConfirmation}
            disabled={moveInProgress}
          >
            CANCEL
          </Button>
        </DialogActions>
      </Dialog>

      <ReportFieldConfigurationDialog
        open={fieldConfigurationOpen}
        onClose={() => setFieldConfigurationOpen(false)}
        onSaved={() =>
          loadDesignerFields(
            template.dataSource?.transactionType,
            template.dataSource?.companyId
          )
        }
        transactionType={template.dataSource?.transactionType}
        authHeaders={authHeaders}
      />

    </Box>
  );
}
